import { Box } from '../Box/Box'
import { FC, useId } from 'react'
import { FormControl } from '../../internal-components/FormControl/FormControl'
import { Stack } from '../Stack/Stack'
import MuiCheckbox, { CheckboxProps as MuiCheckboxProps } from '@mui/material/Checkbox'
import MuiFormHelperText from '@mui/material/FormHelperText'
import MuiFormLabel from '@mui/material/FormLabel'

export interface CheckboxProps
  extends Pick<
    MuiCheckboxProps,
    | 'checked'
    | 'color'
    | 'defaultChecked'
    | 'id'
    | 'indeterminate'
    | 'inputProps'
    | 'inputRef'
    | 'onChange'
    | 'size'
    | 'icon'
    | 'checkedIcon'
    | 'sx'
    | 'aria-label'
  > {
  alignment?: 'top' | 'center'
  inputProps?: MuiCheckboxProps['inputProps'] & {
    'data-testid'?: string
  }
  disabled?: boolean
  error?: boolean
  focused?: boolean
  helperText?: string
  label: string | JSX.Element
  required?: boolean
  hiddenLabel?: boolean
  labelPlacement?: 'end' | 'bottom'
}

/**
 * Checkboxes allow the user to select one or more items from a set.
 *
 * More info can be found at: https://mui.com/material-ui/react-checkbox/
 */
export const Checkbox: FC<CheckboxProps> = ({
  alignment = 'top',
  color = 'primary',
  defaultChecked,
  inputProps,
  inputRef,
  indeterminate,
  size,
  checked,
  label,
  onChange,
  disabled,
  error,
  focused,
  required,
  helperText,
  hiddenLabel,
  icon,
  checkedIcon,
  labelPlacement = 'end',
  sx,
  ...rest
}: CheckboxProps) => {
  const inputId = useId()
  const helperTextId = useId()

  return (
    <FormControl disabled={disabled} error={error} focused={focused} required={required}>
      <Stack
        direction={labelPlacement === 'end' ? 'row' : 'column'}
        sx={{
          alignItems: alignment === 'center' ? 'center' : undefined,
          justifyContent: alignment === 'center' ? 'center' : undefined
        }}
      >
        <MuiCheckbox
          id={inputId}
          color={color}
          defaultChecked={defaultChecked}
          inputProps={{
            ...inputProps,
            ...(helperText && { 'aria-describedby': helperTextId }),
            'aria-invalid': error,
            ...rest
          }}
          inputRef={inputRef}
          indeterminate={indeterminate}
          onChange={onChange}
          size={size}
          checked={checked}
          sx={{
            ...sx,
            height: 'fit-content',
            marginLeft: labelPlacement === 'bottom' ? 0 : undefined
          }}
          icon={icon}
          checkedIcon={checkedIcon}
        />
        <Box sx={{ ...(alignment === 'top' && { pt: size === 'small' ? 1.875 : 2.25 }) }}>
          {!hiddenLabel && (
            <MuiFormLabel
              htmlFor={inputId}
              color={color}
              sx={{ fontSize: labelPlacement === 'bottom' ? '0.75rem' : '1rem' }}
            >
              {label}
            </MuiFormLabel>
          )}
          {helperText && (
            <MuiFormHelperText id={helperTextId} {...(error && { role: 'alert' })}>
              {helperText}
            </MuiFormHelperText>
          )}
        </Box>
      </Stack>
    </FormControl>
  )
}

declare module '@mui/material/Checkbox' {
  interface CheckboxPropsColorOverrides {
    default: false
    primary: true
    secondary: true
    error: false
    info: false
    success: false
    warning: false
  }
}
